import { splitOnMatches, type ITextSegment } from '@orn/shared-web/utils/splitOnMatches';
import * as React from 'react';

interface IHighlightedSegmentsProps {
  segments: ITextSegment[];
}

/**
 * Renders already-split text, wrapping each hit in a `<mark>`. Use it when the
 * caller has cut the text on something else first (e.g. the ids inside it) and
 * a whole-string {@link HighlightedText} would have to re-find the hits within
 * every piece — which loses a hit that straddles two of them.
 *
 * The `<mark>` carries explicit colors because the browser default (black on
 * yellow) is unreadable against the MOPS dark surfaces and ignores the theme.
 */
export const HighlightedSegments = ({ segments }: IHighlightedSegmentsProps): React.ReactElement => (
  <>
    {segments.map((segment, i) =>
      segment.isMatch ? (
        <mark key={i} className="bg-bg-warning-solid text-fg-primary rounded-[1px] px-px">
          {segment.text}
        </mark>
      ) : (
        <React.Fragment key={i}>{segment.text}</React.Fragment>
      )
    )}
  </>
);

interface IHighlightedTextProps {
  text: string;
  /** Active search term; `null` / empty renders `text` untouched. */
  query?: string | null;
}

/**
 * Renders `text`, wrapping every case-insensitive occurrence of `query` in a
 * `<mark>`. With no query it renders the string unchanged and adds no elements,
 * so it is safe to use unconditionally in place of a bare string.
 *
 * Deliberately hook-free: `JsonView` mounts one of these per key and per
 * primitive, so a transcript renders thousands. The no-query path has to cost
 * nothing but a string compare, which rules out memoizing.
 */
const HighlightedText = ({ text, query }: IHighlightedTextProps): React.ReactElement => {
  if (query === null || query === undefined || query.length === 0) {
    return <>{text}</>;
  }
  const segments = splitOnMatches(text, query);
  if (segments.length === 1 && !segments[0].isMatch) {
    return <>{text}</>;
  }
  return <HighlightedSegments segments={segments} />;
};

export default HighlightedText;
